"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Download, Loader2, Lock, Minus, Plus } from "lucide-react";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist/types/src/display/api";
import type { ResumeDocument } from "@/types/cv";
import { ResumePdfDocument } from "@/lib/cv-builder/pdf/resume-pdf";
import { withCvBuilderAnalyticsContext } from "@/components/cv-builder/cv-builder-analytics";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";

interface PdfPreviewProps {
  resume: ResumeDocument;
  analyticsContext?: UmamiEventData;
  fileName?: string;
  showDownloadButton?: boolean;
  // Download gating: building + preview are always free; the actual download can
  // require sign-up. When canDownload is false, the button triggers onLockedDownload.
  canDownload?: boolean;
  onLockedDownload?: () => void;
  // Optional pre-download gate for authenticated flows that still want to
  // surface an upgrade prompt before the free download starts.
  onDownloadAttempt?: () => boolean;
  // Debounce PDF rebuilds for live (type-as-you-go) previews.
  debounceMs?: number;
  viewerClassName?: string;
  downloadSource?: string;
  autoDownloadWhenReady?: boolean;
  onAutoDownloadStarted?: () => void;
  lockedButtonLabel?: string;
  lockedHelperText?: string | null;
}

export interface PdfPreviewHandle {
  download: () => void;
}

type PdfJsModule = typeof import("pdfjs-dist");

let pdfJsWorkerConfigured = false;

async function loadPdfjsForPreview(): Promise<PdfJsModule> {
  const pdfjs = await import("pdfjs-dist");
  if (!pdfJsWorkerConfigured) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url,
    ).toString();
    pdfJsWorkerConfigured = true;
  }
  return pdfjs;
}

function cancelRenderTasks(tasks: RenderTask[]) {
  for (const task of tasks) {
    task.cancel();
  }
}

export function PdfJsPreview({ data }: { data: Uint8Array }) {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fitScale, setFitScale] = useState<number | null>(null);
  const [isRendering, setIsRendering] = useState(true);
  const [error, setError] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const firstPageWidthRef = useRef<number | null>(null);
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);
  const renderTasksRef = useRef<RenderTask[]>([]);

  useEffect(() => {
    let cancelled = false;
    let loadedDoc: PDFDocumentProxy | null = null;
    setPdfDoc(null);
    setPageCount(0);
    setError(false);
    setIsRendering(true);
    setFitScale(null);
    firstPageWidthRef.current = null;

    const load = async () => {
      try {
        const pdfjs = await loadPdfjsForPreview();
        const doc = await pdfjs.getDocument({ data: data.slice() }).promise;
        loadedDoc = doc;
        if (cancelled) {
          await doc.cleanup();
          return;
        }
        setPdfDoc(doc);
        setPageCount(doc.numPages);
      } catch (err) {
        console.error("Failed to load PDF preview", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setIsRendering(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      cancelRenderTasks(renderTasksRef.current);
      renderTasksRef.current = [];
      void loadedDoc?.cleanup();
    };
  }, [data]);

  const renderScale = fitScale === null ? null : fitScale * zoom;

  useEffect(() => {
    if (!pdfDoc || pageCount === 0 || renderScale === null) return;

    let cancelled = false;
    cancelRenderTasks(renderTasksRef.current);
    renderTasksRef.current = [];
    setIsRendering(true);

    const renderPages = async () => {
      try {
        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
          if (cancelled) return;

          const canvas = canvasRefs.current[pageNumber - 1];
          if (!canvas) continue;

          const page = await pdfDoc.getPage(pageNumber);
          const viewport = page.getViewport({ scale: renderScale });
          const outputScale = window.devicePixelRatio || 1;

          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;

          const transform =
            outputScale !== 1
              ? [outputScale, 0, 0, outputScale, 0, 0]
              : undefined;
          const renderTask = page.render({
            canvas,
            viewport,
            transform,
          });
          renderTasksRef.current.push(renderTask);
          await renderTask.promise;
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to render PDF preview", err);
          setError(true);
        }
      } finally {
        if (!cancelled) setIsRendering(false);
      }
    };

    void renderPages();

    return () => {
      cancelled = true;
      cancelRenderTasks(renderTasksRef.current);
      renderTasksRef.current = [];
    };
  }, [pdfDoc, pageCount, renderScale]);

  useEffect(() => {
    if (!pdfDoc || pageCount === 0) return;

    let cancelled = false;

    const updateFitScale = async () => {
      const container = scrollAreaRef.current;
      if (!container) return;

      if (!firstPageWidthRef.current) {
        const firstPage = await pdfDoc.getPage(1);
        if (cancelled) return;
        firstPageWidthRef.current = firstPage.getViewport({ scale: 1 }).width;
      }

      const pageWidth = firstPageWidthRef.current;
      if (!pageWidth) return;

      const horizontalPadding = container.clientWidth >= 640 ? 32 : 24;
      const availableWidth = Math.max(
        120,
        container.clientWidth - horizontalPadding,
      );
      setFitScale(Math.min(1, Math.max(0.25, availableWidth / pageWidth)));
    };

    void updateFitScale();

    const resizeObserver = new ResizeObserver(() => {
      void updateFitScale();
    });

    if (scrollAreaRef.current) {
      resizeObserver.observe(scrollAreaRef.current);
    }

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
    };
  }, [pdfDoc, pageCount]);

  const canZoomOut = zoom > 0.75;
  const canZoomIn = zoom < 2.5;

  if (error) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-destructive">
        Gagal menampilkan preview PDF. Coba edit lalu buka lagi.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-12 items-center justify-between gap-3 border-b border-border bg-[#323639] px-3 text-xs text-white">
        <div>
          {pageCount > 0 ? `${pageCount} halaman` : "Memuat preview..."}
        </div>
        <div className="flex items-center gap-2">
          {isRendering ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!canZoomOut}
            onClick={() => setZoom((current) => Math.max(0.75, current - 0.25))}
            className="h-8 w-8 text-white hover:bg-white/10 hover:text-white"
            aria-label="Perkecil preview"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-12 text-center">{Math.round(zoom * 100)}%</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!canZoomIn}
            onClick={() => setZoom((current) => Math.min(2.5, current + 0.25))}
            className="h-8 w-8 text-white hover:bg-white/10 hover:text-white"
            aria-label="Perbesar preview"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div
        ref={scrollAreaRef}
        className="min-w-0 flex-1 touch-pan-x touch-pan-y touch-pinch-zoom overflow-auto overscroll-contain bg-[#525659] p-3 sm:p-4"
      >
        <div className="mx-auto flex w-max min-w-0 flex-col items-center gap-4">
          {Array.from({ length: pageCount }, (_, index) => (
            <canvas
              key={index}
              ref={(canvas) => {
                canvasRefs.current[index] = canvas;
              }}
              className="block bg-white shadow-xl"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Renders the resume to a real-text PDF, then previews it with PDF.js so the
// toolbar is controlled by the app and does not expose a download button.
export const PdfPreview = forwardRef<PdfPreviewHandle, PdfPreviewProps>(
  function PdfPreview(
    {
      resume,
      analyticsContext,
      fileName = "hyred-cv-ats.pdf",
      showDownloadButton = true,
      canDownload = true,
      onLockedDownload,
      onDownloadAttempt,
      debounceMs = 400,
      viewerClassName,
      downloadSource = "builder_result",
      autoDownloadWhenReady = false,
      onAutoDownloadStarted,
      lockedButtonLabel = "Simpan & download PDF",
      lockedHelperText = "Buat akun gratis agar CV kamu tidak hilang.",
    },
    ref,
  ) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [isBuilding, setIsBuilding] = useState(true);
  const [error, setError] = useState(false);
  const autoDownloadTriggeredRef = useRef(false);
  const pendingDownloadRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    autoDownloadTriggeredRef.current = false;
    setIsBuilding(true);
    setError(false);
    setPdfData(null);

    const build = async () => {
      try {
        const { pdf } = await import("@react-pdf/renderer");
        const blob = await pdf(<ResumePdfDocument resume={resume} />).toBlob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setPdfData(new Uint8Array(await blob.arrayBuffer()));
        setBlobUrl(createdUrl);
      } catch (err) {
        console.error("Failed to build resume PDF", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setIsBuilding(false);
      }
    };

    const handle = setTimeout(build, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(handle);
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [resume, debounceMs]);

  const downloadPdf = useCallback(
    (trigger: "auto" | "click") => {
      if (!blobUrl) return false;
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      trackEvent(
        "builder_pdf_download",
        withCvBuilderAnalyticsContext(analyticsContext, {
          source: downloadSource,
          trigger,
        }),
      );
      return true;
    },
    [analyticsContext, blobUrl, downloadSource, fileName],
  );

  const requestDownload = useCallback(() => {
    if (blobUrl && !isBuilding) {
      downloadPdf("click");
      return;
    }

    pendingDownloadRef.current = true;
  }, [blobUrl, downloadPdf, isBuilding]);

  useImperativeHandle(ref, () => ({ download: requestDownload }), [
    requestDownload,
  ]);

  useEffect(() => {
    if (!pendingDownloadRef.current || !blobUrl || isBuilding) {
      return;
    }

    pendingDownloadRef.current = false;
    downloadPdf("click");
  }, [blobUrl, downloadPdf, isBuilding]);

  const handleDownload = () => {
    if (onDownloadAttempt && !onDownloadAttempt()) {
      return;
    }

    trackEvent(
      "builder_download_clicked",
      withCvBuilderAnalyticsContext(analyticsContext, {
        source: downloadSource,
        authed: canDownload,
      }),
    );
    if (!canDownload) {
      onLockedDownload?.();
      return;
    }
    if (!blobUrl) return;
    downloadPdf("click");
  };

  useEffect(() => {
    if (
      !autoDownloadWhenReady ||
      !canDownload ||
      !blobUrl ||
      isBuilding ||
      autoDownloadTriggeredRef.current
    ) {
      return;
    }

    autoDownloadTriggeredRef.current = true;
    if (downloadPdf("auto")) {
      onAutoDownloadStarted?.();
    }
  }, [
    autoDownloadWhenReady,
    blobUrl,
    canDownload,
    downloadPdf,
    isBuilding,
    onAutoDownloadStarted,
  ]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Pratinjau persis seperti file PDF yang kamu download. Gunakan zoom,
          lalu geser di dalam preview untuk lihat detail.
        </p>
        {showDownloadButton ? (
          <div className="w-full shrink-0 sm:w-auto">
            <Button
              onClick={handleDownload}
              disabled={canDownload && (!blobUrl || isBuilding)}
              className="w-full sm:w-auto"
            >
              {canDownload ? (
                isBuilding ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}
              {canDownload ? "Unduh PDF" : lockedButtonLabel}
            </Button>
            {!canDownload && lockedHelperText ? (
              <p className="mt-2 text-center text-xs text-muted-foreground sm:text-right">
                {lockedHelperText}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          "relative h-[70vh] min-h-[420px] overflow-hidden rounded-xl border border-border bg-muted/30",
          viewerClassName,
        )}
      >
        {isBuilding ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Menyiapkan CV kamu...
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-destructive">
            Gagal membuat PDF. Coba edit lalu buka lagi.
          </div>
        ) : pdfData ? (
          <PdfJsPreview data={pdfData} />
        ) : null}
      </div>

    </div>
  );
  },
);
